import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useRestaurant } from '@/contexts/RestaurantContext';
import type { User } from '@supabase/supabase-js';

/**
 * User Types - Simplified Binary Hierarchy
 * 
 * MASTER ADMIN (Dono): Pays subscription, switches stores, manages units/credentials
 * UNIT MANAGER (Gerente): Access only their specific unit, no settings access
 * SUPPORT: Internal support role for Alacart team
 */
export type UserType = 'master_admin' | 'unit_manager' | 'support';

// Legacy roles kept for backward compatibility with database
export type AppRole = 'admin' | 'chef' | 'cook' | 'support';

export interface UserPermissions {
  // User identification
  user: User | null | undefined;
  userType: UserType;
  isLoading: boolean;
  
  // Binary hierarchy
  isMasterAdmin: boolean;
  isUnitManager: boolean;
  isSupport: boolean;
  
  // Legacy compatibility (mapped to new system)
  isOwner: boolean;
  isAdmin: boolean;
  isChef: boolean;
  isCook: boolean;
  isChefOrAdmin: boolean;
  currentRole: AppRole;
  roles: AppRole[];
  
  // Simplified permissions
  canSwitchStores: boolean;
  canManageUnits: boolean;
  canManageCredentials: boolean;
  canAccessSettings: boolean;
  canManageSubscription: boolean;
  canAccessOperations: boolean;
  
  // Legacy permissions (all mapped based on binary type)
  canSeeDashboard: boolean;
  canSeeCosts: boolean;
  canSeeCostCenter: boolean;
  canSeePricing: boolean;
  canManageSales: boolean;
  canManageIngredients: boolean;
  canManageRecipes: boolean;
  canManageSettings: boolean;
  canManageUsers: boolean;
  canViewRecipes: boolean;
  canViewProduction: boolean;
  canViewReports: boolean;
  canAccessHub: boolean;
  
  // Helpers
  hasRole: (roles: AppRole[]) => boolean;
  refreshRole: () => void;
}

export const useUserRole = (): UserPermissions => {
  const queryClient = useQueryClient();
  
  // Fetch current user
  const { data: user } = useQuery({
    queryKey: ['current-user'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  // Try to get currentRestaurantId from context or localStorage
  let currentRestaurantId: string | null = null;
  let isOwnerOfAny = false;
  let userRole: string | null = null;
  
  try {
    const context = useRestaurant();
    currentRestaurantId = context?.currentRestaurantId || null;
    isOwnerOfAny = context?.isOwnerOfAny || false;
    userRole = context?.userRole || null;
  } catch {
    // If not inside provider, use localStorage
    currentRestaurantId = localStorage.getItem('currentRestaurantId');
  }

  // Check if user is support (via user_roles table)
  const { data: supportRoleData } = useQuery({
    queryKey: ['user-support-role', user?.id],
    queryFn: async () => {
      if (!user) return false;
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'support')
        .maybeSingle();
      return !!data;
    },
    enabled: !!user,
  });

  // NEW: Query to check if user owns ANY restaurant (global ownership check)
  // This ensures isMasterAdmin is correct even when viewing secondary units
  const { data: ownsAnyRestaurant } = useQuery({
    queryKey: ['user-owns-any-restaurant', user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      const { count } = await supabase
        .from('restaurants')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id);
      return (count || 0) > 0;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch role context (for loading state)
  const { isLoading } = useQuery({
    queryKey: ['user-role-context', user?.id, currentRestaurantId],
    queryFn: async () => {
      if (!user?.id || !currentRestaurantId) return null;
      
      // Check if owner
      const { data: restaurant } = await supabase
        .from('restaurants')
        .select('user_id')
        .eq('id', currentRestaurantId)
        .single();

      if (restaurant?.user_id === user.id) {
        return { type: 'master_admin' as UserType };
      }

      // Check membership
      const { data: membership } = await supabase
        .from('restaurant_members')
        .select('role')
        .eq('user_id', user.id)
        .eq('restaurant_id', currentRestaurantId)
        .maybeSingle();

      return { type: 'unit_manager' as UserType, role: membership?.role };
    },
    enabled: !!user?.id && !!currentRestaurantId,
    staleTime: 1000 * 60 * 5,
  });

  // Determine user type using binary logic
  // FIXED: Use ownsAnyRestaurant query to ensure Master Admin is identified correctly
  // even when viewing a secondary unit they don't directly own
  const isSupport = supportRoleData === true;
  const isMasterAdmin = (ownsAnyRestaurant === true || isOwnerOfAny) && !isSupport;
  const isUnitManager = !isMasterAdmin && !isSupport;
  
  // Determine user type
  const userType: UserType = isSupport 
    ? 'support' 
    : isMasterAdmin 
      ? 'master_admin' 
      : 'unit_manager';

  // Simplified permissions based on binary hierarchy
  const canSwitchStores = isMasterAdmin || isSupport;
  const canManageUnits = isMasterAdmin || isSupport;
  const canManageCredentials = isMasterAdmin || isSupport;
  const canAccessSettings = isMasterAdmin || isSupport;
  const canManageSubscription = isMasterAdmin; // Only owner can manage subscription
  const canAccessOperations = true; // Both types can operate (Sales, Stock, Production)

  // Map to legacy permissions - UPDATED for Unit Manager access
  // Master Admin: EVERYTHING
  // Unit Manager: Operational access (Dashboard, Sales, Stock, Menu, Production, Reports of their unit)
  const canSeeDashboard = true; // All authenticated users see dashboard
  const canSeeCosts = true; // Unit Manager can see costs
  const canSeeCostCenter = isMasterAdmin || isSupport; // Only admin sees cost center management
  const canSeePricing = true; // All can see pricing
  const canManageSales = true; // Both can manage sales
  const canManageIngredients = true; // Both can manage ingredients
  const canManageRecipes = true; // Both can manage recipes
  const canManageSettings = isMasterAdmin || isSupport; // Only admin manages settings
  const canManageUsers = isMasterAdmin || isSupport;
  const canViewRecipes = true; // Both can view recipes
  const canViewProduction = true; // Both can view production
  const canViewReports = true; // Unit Manager can see reports of their unit
  const canAccessHub = isMasterAdmin || isSupport;

  // Legacy compatibility
  const isOwner = isMasterAdmin;
  const isAdmin = isMasterAdmin || isSupport;
  const isChef = false; // No more chef role
  const isCook = isUnitManager;
  const isChefOrAdmin = isAdmin;
  const currentRole: AppRole = isSupport ? 'support' : isMasterAdmin ? 'admin' : 'cook';
  const roles: AppRole[] = [currentRole];

  const hasRole = (checkRoles: AppRole[]): boolean => {
    return checkRoles.includes(currentRole);
  };

  const refreshRole = () => {
    queryClient.invalidateQueries({ queryKey: ['user-role-context'] });
    queryClient.invalidateQueries({ queryKey: ['user-support-role'] });
  };

  return {
    user,
    userType,
    isLoading,
    
    // Binary hierarchy
    isMasterAdmin,
    isUnitManager,
    isSupport,
    
    // Legacy compatibility
    isOwner,
    isAdmin,
    isChef,
    isCook,
    isChefOrAdmin,
    currentRole,
    roles,
    
    // Simplified permissions
    canSwitchStores,
    canManageUnits,
    canManageCredentials,
    canAccessSettings,
    canManageSubscription,
    canAccessOperations,
    
    // Legacy permissions
    canSeeDashboard,
    canSeeCosts,
    canSeeCostCenter,
    canSeePricing,
    canManageSales,
    canManageIngredients,
    canManageRecipes,
    canManageSettings,
    canManageUsers,
    canViewRecipes,
    canViewProduction,
    canViewReports,
    canAccessHub,
    
    hasRole,
    refreshRole,
  };
};

// Hook to get translated role label
export const useRoleLabel = () => {
  const labels: Record<UserType, string> = {
    master_admin: 'Administrador',
    unit_manager: 'Gerente de Unidade',
    support: 'Suporte',
  };

  const legacyLabels: Record<AppRole, string> = {
    admin: 'Administrador',
    chef: 'Chef',
    cook: 'Cozinheiro',
    support: 'Suporte',
  };

  const getBadgeVariant = (role: AppRole | UserType): 'default' | 'secondary' | 'outline' => {
    if (role === 'admin' || role === 'master_admin') return 'default';
    if (role === 'support') return 'secondary';
    return 'outline';
  };

  return { labels, legacyLabels, getBadgeVariant };
};
